import type { LocalTrack, RemoteTrack, Room } from 'livekit-client'

/**
 * Сбор WebRTC-статистики по трекам демонстрации экрана.
 *
 * Модуль только читает состояние и ничего не меняет: ни треков, ни кодеков, ни
 * прав. Всё сводится к компактным снимкам, потому что сырой `RTCStatsReport`
 * весит десятки килобайт и в Loki ему не место.
 *
 * Зачем это нужно. На разборе двух инцидентов (октябрь 2026) выяснилось, что
 * браузер всё время знал главный факт — кадры приходят, но ни один не
 * декодируется, — а серверные логи видели только живой поток. Разбор занял часы
 * ручных замеров записи. Одна строка `screen-share-inbound` с полями
 * `framesReceived` и `framesDecoded` заменила бы всю эту работу.
 */

/** Один элемент отчёта `getStats`: набор полей без гарантии состава. */
type StatsEntry = Record<string, unknown>

/** Снимок отправителя: чем кодируем экран и с каким профилем. */
export type ScreenShareSenderSnapshot = {
  trackSid: string
  mimeType: string | null
  profileLevelId: string | null
  encoderImplementation: string | null
  frameWidth: number | null
  frameHeight: number | null
  framesPerSecond: number | null
  framesEncoded: number | null
  qualityLimitationReason: string | null
  targetBitrate: number | null
}

/** Снимок получателя: дошли ли кадры и удалось ли их разобрать.
 *
 * `packetsReceived` и `pliCount` нужны детектору отказа декодера: растущие
 * пакеты при нулевых `framesDecoded` и `keyFramesDecoded` — это наш кейс H.264,
 * а всплеск PLI при нулевых `framesReceived` ловит отказ ещё раньше.
 */
export type ScreenShareReceiverSnapshot = {
  trackSid: string
  publisherIdentity: string
  mimeType: string | null
  profileLevelId: string | null
  inboundRtpAvailable: boolean
  packetsReceivedAvailable: boolean
  framesReceivedAvailable: boolean
  framesDecodedAvailable: boolean
  packetsReceived: number
  framesReceived: number
  framesDecoded: number
  keyFramesDecoded: number
  framesDropped: number
  freezeCount: number
  packetsLost: number
  pliCount: number
  jitter: number | null
  decoderImplementation: string | null
  frameWidth: number | null
  frameHeight: number | null
  undecodable: boolean
}

/** Снимок транспорта: качество пути между клиентом и SFU. */
export type ScreenShareTransportSnapshot = {
  role: 'publisher' | 'subscriber'
  state: string | null
  roundTripTime: number | null
  availableOutgoingBitrate: number | null
  availableIncomingBitrate: number | null
  localCandidateType: string | null
  remoteCandidateType: string | null
}

/**
 * Минимальный срез `engine.pcManager`, который нужен для доступа к peer connection.
 *
 * Публичного метода SDK не даёт, поэтому читаем закрытое поле `_pc` и на каждом
 * шаге проверяем, что объект живой. Смена внутренностей SDK не должна ронять
 * встречу: не нашли — просто не логируем транспорт.
 */
type EngineLike = {
  pcManager?: {
    publisher?: { _pc?: RTCPeerConnection }
    subscriber?: { _pc?: RTCPeerConnection }
  }
}

/**
 * Читает числовое поле отчёта, отбрасывая всё, что не число.
 *
 * @param entry - элемент отчёта `getStats`
 * @param key - имя поля, например `framesDecoded`
 */
function readNumber(entry: StatsEntry | undefined, key: string): number | null {
  const value = entry?.[key]

  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * Читает строковое поле отчёта.
 *
 * @param entry - элемент отчёта `getStats`
 * @param key - имя поля, например `encoderImplementation`
 */
function readString(entry: StatsEntry | undefined, key: string): string | null {
  const value = entry?.[key]

  return typeof value === 'string' && value !== '' ? value : null
}

/**
 * Раскладывает отчёт в словарь по id, чтобы связать записи по ссылкам.
 *
 * `codecId` и `candidateId` в статистике — это id соседних записей, поэтому без
 * словаря не достать ни профиль кодека, ни типы кандидатов.
 *
 * @param report - отчёт `getStats` от трека или peer connection
 */
function indexReport(report: RTCStatsReport): Map<string, StatsEntry> {
  const entries = new Map<string, StatsEntry>()

  report.forEach((value, id) => {
    entries.set(id, value as StatsEntry)
  })

  return entries
}

/**
 * Находит видео-запись нужного типа: отправленную или полученную.
 *
 * У отправителя с экраном и камерой в одном отчёте бывает две записи, поэтому
 * сначала ищем `mediaSource: 'screen'`, а при неудаче берём первую видео.
 *
 * @param entries - проиндексированный отчёт
 * @param type - `outbound-rtp` или `inbound-rtp`
 */
function pickVideoEntry(
  entries: Map<string, StatsEntry>,
  type: 'outbound-rtp' | 'inbound-rtp',
): StatsEntry | undefined {
  const videoEntries: StatsEntry[] = []

  entries.forEach((entry) => {
    if (entry.type === type && entry.kind === 'video') {
      videoEntries.push(entry)
    }
  })

  if (videoEntries.length <= 1) {
    return videoEntries[0]
  }

  return videoEntries.find((entry) => entry.mediaSource === 'screen') ?? videoEntries[0]
}

/**
 * Достаёт `profile-level-id` из строки fmtp согласованного кодека.
 *
 * Профиль решает, сможет ли браузер разобрать поток: `42e01f` — constrained
 * baseline, `4d001f` — main, `64001f` — high. Значение `f4…` (High 4:4:4)
 * браузерные декодеры не поддерживают, и именно это давало чёрный экран при
 * живом потоке.
 *
 * @param fmtpLine - строка `sdpFmtpLine` из записи кодека
 */
export function extractProfileLevelId(fmtpLine?: string | null): string | null {
  if (!fmtpLine) {
    return null
  }

  const match = /profile-level-id=([0-9a-fA-F]{4,6})/.exec(fmtpLine)

  return match?.[1]?.toLowerCase() ?? null
}

/**
 * Читает согласованный кодек из параметров отправителя.
 *
 * Запасной путь на случай, когда в отчёте ещё нет записи `codec`: сразу после
 * публикации `sdpFmtpLine` доступен только через `getParameters()`.
 *
 * @param track - локальный трек, у которого уже есть sender
 */
function readSenderCodec(track: LocalTrack): StatsEntry | undefined {
  const codec = track.sender?.getParameters()?.codecs?.[0]

  if (!codec) {
    return undefined
  }

  return { mimeType: codec.mimeType, sdpFmtpLine: codec.sdpFmtpLine }
}

/**
 * Находит запись кодека по ссылке `codecId` из rtp-записи.
 *
 * @param entries - проиндексированный отчёт
 * @param rtpEntry - запись `outbound-rtp` или `inbound-rtp`
 */
function readCodecEntry(
  entries: Map<string, StatsEntry>,
  rtpEntry: StatsEntry | undefined,
): StatsEntry | undefined {
  const codecId = readString(rtpEntry, 'codecId')

  return codecId ? entries.get(codecId) : undefined
}

/**
 * Собирает снимок отправителя по локальному экранному треку.
 *
 * Смысл снимка — зафиксировать, чем именно мы кодируем экран: реализацию
 * энкодера и профиль H.264. Причина инцидента в том, что «кодек поддерживается»
 * и «битстрим совместим с декодером получателя» — разные вещи, и по логу это
 * видно только если записать профиль.
 *
 * @param track - локальный трек демонстрации экрана
 * @param trackSid - sid публикации, чтобы связать запись с треком
 */
export async function collectSenderSnapshot(
  track: LocalTrack,
  trackSid: string,
): Promise<ScreenShareSenderSnapshot | null> {
  try {
    const report = await track.getRTCStatsReport()

    if (!report) {
      return null
    }

    const entries = indexReport(report)
    const outbound = pickVideoEntry(entries, 'outbound-rtp')
    const codec = readCodecEntry(entries, outbound) ?? readSenderCodec(track)
    const targetBitrate = readNumber(outbound, 'targetBitrate')

    return {
      trackSid,
      mimeType: readString(codec, 'mimeType'),
      profileLevelId: extractProfileLevelId(readString(codec, 'sdpFmtpLine')),
      encoderImplementation: readString(outbound, 'encoderImplementation'),
      frameWidth: readNumber(outbound, 'frameWidth'),
      frameHeight: readNumber(outbound, 'frameHeight'),
      framesPerSecond: readNumber(outbound, 'framesPerSecond'),
      framesEncoded: readNumber(outbound, 'framesEncoded'),
      qualityLimitationReason: readString(outbound, 'qualityLimitationReason'),
      targetBitrate: targetBitrate === null ? null : Math.round(targetBitrate),
    }
  } catch {
    return null
  }
}

/**
 * Собирает снимок получателя и сам помечает поток как не декодируемый.
 *
 * Флаг `undecodable` — главный смысл всей затеи: кадры пришли, но ни один не
 * разобран. Потери при этом могут быть нулевыми, поэтому смотреть надо именно
 * на пару `framesReceived` и `framesDecoded`, а не на `packetsLost`.
 *
 * @param track - удалённый трек демонстрации экрана
 * @param publisherIdentity - идентичность автора показа
 * @param trackSid - sid публикации
 */
export async function collectReceiverSnapshot(
  track: RemoteTrack,
  publisherIdentity: string,
  trackSid: string,
): Promise<ScreenShareReceiverSnapshot | null> {
  try {
    const report = await track.getRTCStatsReport()

    if (!report) {
      return null
    }

    const entries = indexReport(report)
    const inbound = pickVideoEntry(entries, 'inbound-rtp')
    const codec = readCodecEntry(entries, inbound)
    const receivedCount = readNumber(inbound, 'framesReceived')
    const decodedCount = readNumber(inbound, 'framesDecoded')
    const packetCount = readNumber(inbound, 'packetsReceived')
    const framesReceived = receivedCount ?? 0
    const framesDecoded = decodedCount ?? 0
    const packetsReceived = packetCount ?? 0
    const jitter = readNumber(inbound, 'jitter')

    return {
      trackSid,
      publisherIdentity,
      mimeType: readString(codec, 'mimeType'),
      profileLevelId: extractProfileLevelId(readString(codec, 'sdpFmtpLine')),
      inboundRtpAvailable: inbound !== undefined,
      packetsReceivedAvailable: packetCount !== null,
      framesReceivedAvailable: receivedCount !== null,
      framesDecodedAvailable: decodedCount !== null,
      packetsReceived,
      framesReceived,
      framesDecoded,
      keyFramesDecoded: readNumber(inbound, 'keyFramesDecoded') ?? 0,
      framesDropped: readNumber(inbound, 'framesDropped') ?? 0,
      freezeCount: readNumber(inbound, 'freezeCount') ?? 0,
      packetsLost: readNumber(inbound, 'packetsLost') ?? 0,
      pliCount: readNumber(inbound, 'pliCount') ?? 0,
      jitter: jitter === null ? null : Math.round(jitter * 10_000) / 10_000,
      decoderImplementation: readString(inbound, 'decoderImplementation'),
      frameWidth: readNumber(inbound, 'frameWidth'),
      frameHeight: readNumber(inbound, 'frameHeight'),
      undecodable: receivedCount !== null && receivedCount > 0 && decodedCount === 0,
    }
  } catch {
    return null
  }
}

/**
 * Проверяет, что значение похоже на живой peer connection.
 *
 * @param value - подозрительный объект из внутренностей SDK
 */
function asPeerConnection(value?: RTCPeerConnection): RTCPeerConnection | undefined {
  if (value && typeof value.getStats === 'function') {
    return value
  }

  return undefined
}

/**
 * Достаёт peer connections комнаты, если SDK оставил их доступными.
 *
 * Статистика уровня трека этого не требует, поэтому функция нужна только для
 * качества пути: RTT, доступной полосы и типов ICE-кандидатов. Ошибки глушим —
 * отсутствие внутренностей SDK не должно ломать диагностику треков.
 *
 * @param room - текущая комната livekit
 */
export function getRoomPeerConnections(room: Room): {
  publisher?: RTCPeerConnection
  subscriber?: RTCPeerConnection
} {
  try {
    // SAFETY: engine и pcManager есть в рантайме livekit-client, но `_pc`
    // объявлен приватным, поэтому пересечение типов схлопывает поле в never.
    // Инвариант: значение либо рабочий peer connection, либо его отсекает
    // asPeerConnection
    const engine = (room as unknown as { engine?: EngineLike }).engine
    const manager = engine?.pcManager

    return {
      publisher: asPeerConnection(manager?.publisher?._pc),
      subscriber: asPeerConnection(manager?.subscriber?._pc),
    }
  } catch {
    return {}
  }
}

/**
 * Выбирает пару кандидатов, по которой реально идёт медиа.
 *
 * Chrome оставляет несколько пар в состоянии `succeeded`, но только одна
 * помечена `nominated` — именно она и определяет путь.
 *
 * @param entries - проиндексированный отчёт peer connection
 */
function pickSelectedCandidatePair(entries: Map<string, StatsEntry>): StatsEntry | undefined {
  const pairs: StatsEntry[] = []

  entries.forEach((entry) => {
    if (entry.type === 'candidate-pair' && entry.state === 'succeeded') {
      pairs.push(entry)
    }
  })

  return pairs.find((entry) => entry.nominated === true) ?? pairs[0]
}

/**
 * Собирает снимки транспорта по доступным peer connections.
 *
 * `currentRoundTripTime` в статистике браузера приходит в секундах, поэтому
 * переводим его в миллисекунды: в логах удобнее сравнивать с `maxRtt` из
 * серверных событий LiveKit.
 *
 * @param room - текущая комната livekit
 */
export async function collectTransportSnapshots(room: Room): Promise<ScreenShareTransportSnapshot[]> {
  const connections = getRoomPeerConnections(room)
  const roles: Array<['publisher' | 'subscriber', RTCPeerConnection | undefined]> = [
    ['publisher', connections.publisher],
    ['subscriber', connections.subscriber],
  ]
  const snapshots: ScreenShareTransportSnapshot[] = []

  for (const [role, connection] of roles) {
    if (!connection) {
      continue
    }

    try {
      const entries = indexReport(await connection.getStats())
      const pair = pickSelectedCandidatePair(entries)

      if (!pair) {
        continue
      }

      const localCandidate = entries.get(readString(pair, 'localCandidateId') ?? '')
      const remoteCandidate = entries.get(readString(pair, 'remoteCandidateId') ?? '')
      const roundTripTime = readNumber(pair, 'currentRoundTripTime') ?? readNumber(pair, 'roundTripTime')

      snapshots.push({
        role,
        state: readString(pair, 'state'),
        roundTripTime: roundTripTime === null ? null : Math.round(roundTripTime * 1000),
        availableOutgoingBitrate: readNumber(pair, 'availableOutgoingBitrate'),
        availableIncomingBitrate: readNumber(pair, 'availableIncomingBitrate'),
        localCandidateType: readString(localCandidate, 'candidateType'),
        remoteCandidateType: readString(remoteCandidate, 'candidateType'),
      })
    } catch {
      // Отчёт peer connection мог не собраться — остальные снимки важнее
    }
  }

  return snapshots
}
